import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { Search, X } from 'react-feather';
import { CapabilitiesContext, LinkContext } from '@/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@/components/Chat/context';
import { FilterButton } from '@/components/Chat.Filters';
import { Tooltip } from '@/components/presentation';
import { Constants } from '@/components/Theme';
import { ChatContextType } from '@/enums/chat';
import { trunc } from '@/utils';
import { HeaderContextContext } from './context';
import { ContextSearch } from './ContextSearch';
import { ChatContextSearchContainer } from './ContextSearch.Container';
import { ContextSelectorContainer } from './ContextSelector.Container';
import { JumpToPage } from './JumpToPage';
import { SimpleContextSelector } from './SimpleContextSelector';

type Props = {
  className?: string;
};

function ChatHeader({ className }: Props) {
  const link = useContext(LinkContext);
  const capabilities = useContext(CapabilitiesContext);

  return (
    <Root className={className}>
      <Wrap>
        {link.initialized && (
          <>
            <Left>
              {capabilities.canApplyFilters && <StyledFilterButton />}
              <SimpleContextSelector />
              <ContextDisplay />
            </Left>

            <Right>
              {capabilities.canViewExternalLinks && <JumpToPage />}
            </Right>
          </>
        )}
      </Wrap>
    </Root>
  );
}

function ContextDisplay() {
  const {
    pendingContextType,
    setPendingContextType,
    pendingEverythingSearch,
    setPendingEverythingSearch,
  } = useContext(HeaderContextContext);
  const session = useContext(ActiveChatSessionContext);

  const handleReset = useCallback(() => {
    setPendingContextType(session.context.type);
  }, [setPendingContextType, session.context.type]);

  const handleEverythingSearch = useCallback(() => {
    setPendingEverythingSearch(true);
  }, [setPendingEverythingSearch]);

  const renderSubject = useCallback(() => {
    if (session.context.type === ChatContextType.Global) {
      return null;
    }

    const maxNameLength = 50;

    if (session.context.metadata.name?.length > maxNameLength) {
      return (
        <Tooltip title={session.context.metadata.name}>
          <Subject onClick={handleReset}>
            {trunc(session.context.metadata.name, maxNameLength)}
          </Subject>
        </Tooltip>
      );
    }

    return (
      <Subject onClick={handleReset}>
        {session.context.metadata.name}
      </Subject>
    );
  }, [handleReset, session.context.metadata, session.context.type]);

  const hasActiveSearch = useMemo(() => {
    if (pendingEverythingSearch) return true;

    if (!pendingContextType) return false;

    return pendingContextType === ChatContextType.KolProfile;
  }, [pendingContextType, pendingEverythingSearch]);

  const hasSubject = useMemo(() => {
    return !!session.context.metadata?.name;
  }, [session.context.metadata]);

  if (hasActiveSearch) {
    return (
      <ChatContextSearchContainer>
        <StyledContextSearch />
      </ChatContextSearchContainer>
    );
  }

  if (session.context.type === ChatContextType.Global) {
    return (
      <StyledSearchIcon
        size={19}
        onClick={handleEverythingSearch} />
    );
  }



  if (!hasSubject) {
    if (session.context.type === ChatContextType.KolProfile) {
      return (
        <StyledSearchIcon
          size={19}
          onClick={() => setPendingContextType(ChatContextType.KolProfile)} />
      );
    }

    return null;
  }

  return (
    <ContextSubjectWrapper>
      {renderSubject()}
      <CloseIconWrapper onClick={handleReset}>
        <CloseIcon size={18} />
      </CloseIconWrapper>
    </ContextSubjectWrapper>
  );
}

function ChatHeaderWrapper() {
  return (
    <ContextSelectorContainer>
      <ChatHeader />
    </ContextSelectorContainer>
  );
}

export { ChatHeaderWrapper as ChatHeader };

const Root = styled.div(({ theme }) => ({
  position: 'sticky',
  top: '0',
  height: `${Constants.Modal.HeaderHeight}px`,
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.gray.light2}`,
  padding: '10px',
  background: theme.palette.white.main,
}));

const Wrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Left = styled.div({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

const Right = styled.div({
  cursor: 'pointer',
  marginRight: '8px',
});

const StyledFilterButton = styled(FilterButton)({});

const StyledContextSearch = styled(ContextSearch)({});

const ContextSubjectWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const CloseIcon = styled(X)(({ theme }) => ({
  color: theme.palette.gray.main,
}));

const CloseIconWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  boxSizing: 'border-box',
  width: 24,
  height: 24,

  cursor: 'pointer',

  marginLeft: 10,

  borderRadius: '50%',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,

    '& svg': {
      color: theme.palette.black.main,
    },
  },
}));

const StyledSearchIcon = styled(Search)(({ theme }) => ({
  marginLeft: 8,
  cursor: 'pointer',
  color: theme.palette.black.main,
}));

const Subject = styled.div(({ theme }) => ({
  // cursor: 'pointer',
  padding: 5,
  boxSizing: 'border-box',
  borderRadius: 4,

  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: 'transparent',

  color: theme.palette.black.main,

  // '&:hover': {
  //   backgroundColor: theme.palette.gray.light2,
  // },
}));